import type { FieldErrors, UseFormHandleSubmit, UseFormRegister } from 'react-hook-form';
import { inputCls, type BankForm } from '../WithdrawModal.types';

const TEXT_FIELDS = [
  ['accountHolderName', 'Account holder name', 'Enter account holder name', 100, 'text', undefined],
  ['accountNumber', 'Account number', 'Enter account number', 25, 'text', 'numeric'],
  ['ifscCode', 'IFSC code', 'Enter IFSC code', 20, 'text', undefined],
  ['bankName', 'Bank name', 'Enter bank name', 100, 'text', undefined],
  ['branchName', 'Branch name', 'Enter branch name', 100, 'text', undefined],
] as const;

type AddBankFormProps = {
  formId: string;
  register: UseFormRegister<BankForm>;
  handleSubmit: UseFormHandleSubmit<BankForm>;
  errors: FieldErrors<BankForm>;
  onSubmit: (data: BankForm) => void;
};

export function AddBankForm({
  formId,
  register,
  handleSubmit,
  errors,
  onSubmit,
}: AddBankFormProps) {
  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      {TEXT_FIELDS.map(([name, label, placeholder, maxLength, fieldType, inputMode]) => (
        <div key={name}>
          <label className="mb-1 block text-xs font-medium text-[var(--dash-muted)]">
            {label}
          </label>
          <input
            type={fieldType}
            inputMode={inputMode}
            maxLength={maxLength}
            placeholder={placeholder}
            className={inputCls(Boolean(errors[name]))}
            {...register(name, {
              required: `${label} is required`,
              maxLength: {
                value: maxLength,
                message: `${label} must be at most ${maxLength} characters`,
              },
              ...(name === 'accountNumber' && {
                pattern: {
                  value: /^\d+$/,
                  message: 'Account number must contain only digits',
                },
              }),
            })}
          />
          {errors[name] ? (
            <p className="mt-1 text-[11px] text-red-500">{errors[name]?.message}</p>
          ) : null}
        </div>
      ))}

      <div>
        <label className="mb-1 block text-xs font-medium text-[var(--dash-muted)]">
          Email
        </label>
        <input
          type="email"
          autoComplete="email"
          className={inputCls(Boolean(errors.email))}
          placeholder="you@example.com"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Enter a valid email',
            },
          })}
        />
        {errors.email ? (
          <p className="mt-1 text-[11px] text-red-500">{errors.email.message}</p>
        ) : null}
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-[var(--dash-muted)]">
          Contact number
        </label>
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          className={inputCls(Boolean(errors.contact))}
          placeholder="10-digit mobile number"
          {...register('contact', {
            required: 'Contact number is required',
            validate: (value) => {
              const digits = String(value || '').replace(/\D/g, '');
              const normalized =
                digits.length > 10 ? digits.slice(-10) : digits;
              return (
                /^[0-9]{10}$/.test(normalized) ||
                'Enter a valid 10-digit mobile number'
              );
            },
          })}
        />
        {errors.contact ? (
          <p className="mt-1 text-[11px] text-red-500">{errors.contact.message}</p>
        ) : null}
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-[var(--dash-muted)]">
          Account type
        </label>
        <select
          className={inputCls(Boolean(errors.accountType))}
          {...register('accountType', { required: 'Account type is required' })}
        >
          <option value="">Select</option>
          <option value="savings">Savings</option>
          <option value="current">Current</option>
        </select>
        {errors.accountType ? (
          <p className="mt-1 text-[11px] text-red-500">
            {errors.accountType.message}
          </p>
        ) : null}
      </div>
    </form>
  );
}
